import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isNativePlatform } from '@/services/platformService';
import { NATIVE_PRICES, WEB_PRICES } from '@/services/revenueCatService';

const ServiceProvidersPage = () => {
    const { t } = useTranslation();
    
    // Dynamic pricing based on platform
    const isNative = isNativePlatform();
    const providerPrice = isNative ? NATIVE_PRICES.provider.annual : WEB_PRICES.provider.annual;
    const setupFee = isNative ? 0 : WEB_PRICES.provider.setup;

    return (
        <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
            {/* Header */}
            <div className="container mx-auto px-4 py-6 max-w-7xl">
                <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-8">
                    <ArrowLeft className="h-5 w-5" />
                    <span>{t('common.backToHome')}</span>
                </Link>
            </div>

            {/* Hero Section */}
            <section className="py-12 bg-gradient-to-r from-pet-orange via-pet-pink to-pet-purple relative overflow-hidden">
                <div className="container mx-auto px-4 max-w-7xl">
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">

                        {/* Left Column: Stats & Trust Badges */}
                        <div className="lg:col-span-2 space-y-8">
                            {/* Animated Stats */}
                            <div className="grid grid-cols-1 gap-6">
                                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:scale-105 transition-transform">
                                    <div className="text-4xl font-bold text-white mb-2">{t('landing.providers.stat1Value')}</div>
                                    <div className="text-white/90 font-medium">{t('landing.providers.stat1Label')}</div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:scale-105 transition-transform">
                                    <div className="text-4xl font-bold text-white mb-2">{t('landing.providers.stat2Value')}</div>
                                    <div className="text-white/90 font-medium">{t('landing.providers.stat2Label')}</div>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:scale-105 transition-transform">
                                    <div className="text-4xl font-bold text-white mb-2">{t('landing.providers.stat3Value')}</div>
                                    <div className="text-white/90 font-medium">{t('landing.providers.stat3Label')}</div>
                                </div>
                            </div>

                            {/* Trust Badges */}
                            <div className="grid grid-cols-1 gap-3">
                                <div className="flex items-center gap-3 text-white">
                                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                        <span className="text-lg">✓</span>
                                    </div>
                                    <span className="text-sm font-medium">{t('landing.providers.trustBadge1')}</span>
                                </div>
                                <div className="flex items-center gap-3 text-white">
                                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                        <span className="text-lg">✓</span>
                                    </div>
                                    <span className="text-sm font-medium">{t('landing.providers.trustBadge2')}</span>
                                </div>
                                <div className="flex items-center gap-3 text-white">
                                    <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                                        <span className="text-lg">✓</span>
                                    </div>
                                    <span className="text-sm font-medium">{t('landing.providers.trustBadge3')}</span>
                                </div>
                            </div>
                        </div>

                        {/* Right Column: Value Proposition */}
                        <div className="lg:col-span-3 space-y-8">
                            {/* Headline */}
                            <div className="space-y-4">
                                <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight">
                                    {t('landing.providers.sectionTitle')}
                                </h1>
                                <p className="text-lg md:text-xl text-white/90">
                                    {t('landing.providers.sectionSubtitle')}
                                </p>
                            </div>

                            {/* Benefits */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105">
                                    <div className="text-2xl mb-2">🎯</div>
                                    <h3 className="text-lg font-semibold text-white mb-2">{t('landing.providers.benefit1Title')}</h3>
                                    <p className="text-sm text-white/80">{t('landing.providers.benefit1Description')}</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105">
                                    <div className="text-2xl mb-2">✅</div>
                                    <h3 className="text-lg font-semibold text-white mb-2">{t('landing.providers.benefit2Title')}</h3>
                                    <p className="text-sm text-white/80">{t('landing.providers.benefit2Description')}</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105">
                                    <div className="text-2xl mb-2">📞</div>
                                    <h3 className="text-lg font-semibold text-white mb-2">{t('landing.providers.benefit3Title')}</h3>
                                    <p className="text-sm text-white/80">{t('landing.providers.benefit3Description')}</p>
                                </div>
                                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105">
                                    <div className="text-2xl mb-2">⭐</div>
                                    <h3 className="text-lg font-semibold text-white mb-2">{t('landing.providers.benefit4Title')}</h3>
                                    <p className="text-sm text-white/80">{t('landing.providers.benefit4Description')}</p>
                                </div>
                            </div>

                            {/* Fifth Benefit - Full Width */}
                            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-5 border border-white/20 hover:bg-white/15 transition-all hover:scale-105">
                                <div className="text-2xl mb-2">📊</div>
                                <h3 className="text-lg font-semibold text-white mb-2">{t('landing.providers.benefit5Title')}</h3>
                                <p className="text-sm text-white/80">{t('landing.providers.benefit5Description')}</p>
                            </div>

                            {/* Pricing Card */}
                            <div className="bg-white rounded-2xl p-6 shadow-xl">
                                <h3 className="text-2xl font-bold text-gray-900 mb-4">💰 {t('landing.providers.pricingTitle')}</h3>
                                <div className="space-y-2 mb-4">
                                    <div className="flex items-baseline gap-2">
                                        <span className="text-3xl font-bold text-pet-purple">
                                            US${providerPrice.toFixed(2)} {t('common.perYear')}
                                        </span>
                                        <span className="text-gray-600">({t('landing.providers.pricingPayment')})</span>
                                    </div>
                                    {setupFee > 0 && (
                                        <div className="text-lg text-gray-700">
                                            + US${setupFee.toFixed(2)} {t('common.oneTimeSetupFee')}
                                        </div>
                                    )}
                                </div>
                                <ul className="space-y-3 mb-6">
                                    <li className="flex items-center gap-2 text-gray-700">
                                        <span className="text-green-500 font-bold">✓</span>
                                        <span>{t('landing.providers.pricingFeature1')}</span>
                                    </li>
                                    <li className="flex items-center gap-2 text-gray-700">
                                        <span className="text-green-500 font-bold">✓</span>
                                        <span>{t('landing.providers.pricingFeature2')}</span>
                                    </li>
                                    <li className="flex items-center gap-2 text-gray-700">
                                        <span className="text-green-500 font-bold">✓</span>
                                        <span>{t('landing.providers.pricingFeature3')}</span>
                                    </li>
                                    <li className="flex items-center gap-2 text-gray-700">
                                        <span className="text-green-500 font-bold">✓</span>
                                        <span>{t('landing.providers.pricingFeature4')}</span>
                                    </li>
                                </ul>
                                <div className="flex flex-col sm:flex-row gap-4 w-full">
                                    <Link to="/register-provider" className="flex-1 min-w-0">
                                        <Button className="w-full bg-pet-purple hover:bg-pet-purple/90 text-white font-semibold py-6 text-base sm:text-lg animate-pulse whitespace-normal leading-tight min-h-[3.5rem]">
                                            {t('landing.providers.ctaPrimary')} →
                                        </Button>
                                    </Link>
                                    <Link to="/services" className="flex-1 min-w-0">
                                        <Button variant="outline" className="w-full border-2 border-pet-purple text-pet-purple hover:bg-pet-purple/10 py-6 text-base sm:text-lg whitespace-normal leading-tight min-h-[3.5rem]">
                                            {t('landing.providers.ctaSecondary')}
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Additional Information Section - REFACTORIZADO */}
            <section className="py-20 bg-white">
                <div className="container mx-auto px-4 max-w-4xl">
                    <div className="prose prose-lg max-w-none">
                        <h2 className="text-3xl font-bold text-gray-900 mb-6">{t('landing.providers.whyJoinTitle')}</h2>
                        <p className="text-gray-700 mb-6">
                            {t('landing.providers.whyJoinDescription')}
                        </p>

                        <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">{t('landing.providers.howItWorksTitle')}</h3>
                        <div className="space-y-4">
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-pet-purple text-white rounded-full flex items-center justify-center font-bold">1</div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">{t('landing.providers.step1Title')}</h4>
                                    <p className="text-gray-700">{t('landing.providers.step1Description')}</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-pet-purple text-white rounded-full flex items-center justify-center font-bold">2</div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">{t('landing.providers.step2Title')}</h4>
                                    <p className="text-gray-700">{t('landing.providers.step2Description')}</p>
                                </div>
                            </div>
                            <div className="flex gap-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-pet-purple text-white rounded-full flex items-center justify-center font-bold">3</div>
                                <div>
                                    <h4 className="font-semibold text-gray-900">{t('landing.providers.step3Title')}</h4>
                                    <p className="text-gray-700">{t('landing.providers.step3Description')}</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 text-center">
                            <Link to="/register-provider">
                                <Button size="lg" className="bg-pet-purple hover:bg-pet-purple/90 text-white text-lg px-8 py-6">
                                    {t('landing.providers.ctaFinal')} →
                                </Button>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default ServiceProvidersPage;